import { ref, computed, watch, onMounted } from 'vue'
import { employeeService, teamService } from '../../services/employeeService.js'
import {
  prospectPreferencesService,
  prospectAssignmentService
} from '../../services/prospectService.js'
import { useProspectHelpers } from './useProspectHelpers'

/**
 * Composable for caching and managing prospect data (preferences, assignments, employees, teams)
 * @param {Object} options
 * @param {import('vue').Ref} options.allProspects - Reactive prospect list from useProspectList
 * @param {boolean} options.includeReleasedAssignments - Whether to include released assignments
 */
export function useProspectDataCache(options = {}) {
  const { allProspects, includeReleasedAssignments = false } = options

  // ==================== Local State ====================

  const prospectPreferences = ref({})
  const prospectAssignments = ref({})
  const isLoadingPreferences = ref({})
  const employeesMap = ref({})
  const teamsMap = ref({})
  const searchQuery = ref('')
  const showCompleted = ref(true)

  // ==================== Helpers ====================

  const helpers = useProspectHelpers({
    prospectPreferences,
    prospectAssignments,
    employeesMap,
    teamsMap
  })

  // ==================== Computed Properties ====================

  const approvedProspects = computed(() => {
    if (!allProspects?.value) return []

    let approved = allProspects.value.filter(p =>
      ['goedgekeurd', 'te_bespreken', 'afgekeurd'].includes(p.workflow_status)
    )

    // Auto-hide: remove done prospects older than 14 days
    approved = approved.filter(p => !helpers.isProspectAutoHidden(p))

    // Apply search filter
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase().trim()
      approved = approved.filter(
        p =>
          (p.organization_name || '').toLowerCase().includes(query) ||
          (p.subject || '').toLowerCase().includes(query) ||
          (p.responsible_employee_name || '').toLowerCase().includes(query)
      )
    }

    // Apply completed filter
    if (!showCompleted.value) {
      approved = approved.filter(p => !helpers.isProspectDone(p))
    }

    // Check if all preferences are loaded for sorting stability
    const allLoaded = approved.every(
      p => prospectPreferences.value[p.id] !== undefined && !isLoadingPreferences.value[p.id]
    )

    return approved.sort((a, b) => {
      if (allLoaded) {
        const aDone = helpers.isProspectDone(a)
        const bDone = helpers.isProspectDone(b)
        if (aDone !== bDone) {
          return aDone ? 1 : -1
        }
      }
      const dateA = new Date(a.reviewed_at || a.start_date || '9999-12-31')
      const dateB = new Date(b.reviewed_at || b.start_date || '9999-12-31')
      return dateB - dateA
    })
  })

  const isFullyLoaded = computed(() => {
    if (!allProspects?.value) return false
    const approved = allProspects.value.filter(
      p =>
        ['goedgekeurd', 'te_bespreken', 'afgekeurd'].includes(p.workflow_status) &&
        !helpers.isProspectAutoHidden(p)
    )
    if (approved.length === 0) return true
    return approved.every(
      p => prospectPreferences.value[p.id] !== undefined && !isLoadingPreferences.value[p.id]
    )
  })

  const allApprovedIds = computed(() => {
    if (!allProspects?.value) return new Set()
    return new Set(
      allProspects.value
        .filter(
          p =>
            ['goedgekeurd', 'te_bespreken', 'afgekeurd'].includes(p.workflow_status) &&
            !helpers.isProspectAutoHidden(p)
        )
        .map(p => p.id)
    )
  })

  // ==================== Data Loading ====================

  async function loadPreferencesAndAssignments(prospectId, callbacks = {}) {
    isLoadingPreferences.value[prospectId] = true

    try {
      const prefs = await prospectPreferencesService.getByProspect(prospectId)
      prospectPreferences.value[prospectId] = prefs || []

      const assignmentsResult = await prospectAssignmentService.getByProspect(prospectId, {
        includeReleased: includeReleasedAssignments
      })
      prospectAssignments.value[prospectId] = assignmentsResult || []

      if (callbacks.onPreferencesLoaded) {
        await callbacks.onPreferencesLoaded(prospectId, prefs || [])
      }

      return { preferences: prefs || [], assignments: assignmentsResult || [] }
    } catch (e) {
      console.error(`Error loading data for prospect ${prospectId}:`, e)
      prospectPreferences.value[prospectId] = []
      prospectAssignments.value[prospectId] = []
      return { preferences: [], assignments: [] }
    } finally {
      isLoadingPreferences.value[prospectId] = false
    }
  }

  async function loadTeamsAndEmployees() {
    try {
      const teams = await teamService.getAll()
      teams.forEach(t => {
        teamsMap.value[t.simplicate_id] = t.name
      })
    } catch (e) {
      console.error('Error loading teams:', e)
    }

    try {
      const employees = await employeeService.getAll({ is_sourceable: 'true' })
      employees.forEach(e => {
        employeesMap.value[e.simplicate_id] = e
      })
    } catch (e) {
      console.error('Error loading employees:', e)
    }
  }

  async function batchLoadPreferencesAndAssignments(ids) {
    ids.forEach(id => {
      isLoadingPreferences.value[id] = true
    })

    try {
      const [prefsMap, assignmentsMap] = await Promise.all([
        prospectPreferencesService.batchGetByProspects(ids),
        prospectAssignmentService.batchGetByProspects(ids, {
          includeReleased: includeReleasedAssignments
        })
      ])

      for (const id of ids) {
        prospectPreferences.value[id] = prefsMap[id] || []
        prospectAssignments.value[id] = assignmentsMap[id] || []
      }
    } catch (e) {
      console.error('Error batch loading prospect data:', e)
      ids.forEach(id => {
        prospectPreferences.value[id] = []
        prospectAssignments.value[id] = []
      })
    } finally {
      ids.forEach(id => {
        isLoadingPreferences.value[id] = false
      })
    }
  }

  async function ensureLoadAllApproved(callbacks = {}) {
    const toLoad = approvedProspects.value.filter(
      p => prospectPreferences.value[p.id] === undefined && !isLoadingPreferences.value[p.id]
    )
    if (toLoad.length === 0) return

    const ids = toLoad.map(p => p.id)
    await batchLoadPreferencesAndAssignments(ids)

    if (callbacks.onPreferencesLoaded) {
      for (const id of ids) {
        await callbacks.onPreferencesLoaded(id, prospectPreferences.value[id] || [])
      }
    }
  }

  function addEmployeeToMap(employee) {
    if (!employeesMap.value[employee.simplicate_id]) {
      employeesMap.value[employee.simplicate_id] = employee
    }
  }

  async function reloadProspectData(prospectId, callbacks = {}) {
    return loadPreferencesAndAssignments(prospectId, callbacks)
  }

  async function reloadAssignmentsOnly(prospectId) {
    try {
      const assignmentsResult = await prospectAssignmentService.getByProspect(prospectId, {
        includeReleased: includeReleasedAssignments
      })
      prospectAssignments.value[prospectId] = assignmentsResult || []
      return assignmentsResult || []
    } catch (e) {
      console.error(`Error reloading assignments for prospect ${prospectId}:`, e)
      return []
    }
  }

  // ==================== Lifecycle ====================

  onMounted(() => {
    loadTeamsAndEmployees()
  })

  watch(
    approvedProspects,
    async prospects => {
      const toLoad = prospects.filter(
        p => prospectPreferences.value[p.id] === undefined && !isLoadingPreferences.value[p.id]
      )
      if (toLoad.length === 0) return

      const ids = toLoad.map(p => p.id)
      await batchLoadPreferencesAndAssignments(ids)
    },
    { immediate: true }
  )

  watch(allApprovedIds, (currentIds, previousIds) => {
    if (!previousIds) return
    for (const id of previousIds) {
      if (!currentIds.has(id)) {
        delete prospectPreferences.value[id]
        delete prospectAssignments.value[id]
      }
    }
  })

  // ==================== Return ====================

  return {
    // Caches
    prospectPreferences,
    prospectAssignments,
    employeesMap,
    teamsMap,
    isLoadingPreferences,

    // Computed
    approvedProspects,
    isFullyLoaded,

    // Filters
    searchQuery,
    showCompleted,

    // Data loading methods
    loadPreferencesAndAssignments,
    loadTeamsAndEmployees,
    ensureLoadAllApproved,
    reloadProspectData,
    reloadAssignmentsOnly,
    addEmployeeToMap,

    // Helper functions (re-exported from useProspectHelpers)
    ...helpers
  }
}
